/**
 * Batch Import Store
 * Manages CSV/Excel file uploads and batch job creation
 */

import { api } from "@/utils/api";
import { useAuthStore } from "@/stores/auth";
import { WSEvent } from "@shared/constants";
import {
  calculateBatchImportStats,
  getBatchImportValidationErrors,
} from "@shared";
import type {
  BatchImportOptions,
  BatchImportPreview,
  BatchImportResult,
  ColumnMapping,
  DetectedVehicle,
} from "@shared/types";
import { defineStore } from "pinia";
import type { Socket } from "socket.io-client";
import { computed, ref } from "vue";

export const useBatchImportStore = defineStore("batchImport", () => {
  // State
  const isUploading = ref(false);
  const isProcessing = ref(false);
  const preview = ref<BatchImportPreview | null>(null);
  const result = ref<BatchImportResult | null>(null);
  const error = ref<string | null>(null);

  // Phase 22.3: Upload Progress
  const uploadProgress = ref<{
    current: number;
    total: number;
    percentage: number;
    message: string;
  } | null>(null);

  // Phase 22.3: File upload progress (pre-processing)
  const fileUploadProgress = ref<{
    loaded: number;
    total: number;
    percentage: number;
    message: string;
  } | null>(null);

  // Phase 22.3: Waiting for server-side processing to start
  const processingPending = ref(false);

  // Phase 22.3: Processing started flag (prevents upload state from overriding)
  const processingStarted = ref(false);

  // Phase 22.3: Step to jump to after processing completes
  const nextStepAfterProcessing = ref<number | null>(null);

  // Phase 22: Duplicate handling
  const selectedDuplicates = ref<Set<number>>(new Set());

  // Inline editing state
  const editedValues = ref<Record<string, Record<string, string>>>({});

  const vehiclesWithValidationErrors = computed(() => {
    if (!preview.value) return [];

    return preview.value.vehicles
      .map((vehicle) => ({
        vehicle,
        errors: getBatchImportValidationErrors(vehicle),
      }))
      .filter(({ errors }) => errors.length > 0);
  });

  const validVehicles = computed<DetectedVehicle[]>(() => {
    if (!preview.value) return [];
    return preview.value.vehicles.filter(
      (vehicle) => getBatchImportValidationErrors(vehicle).length === 0,
    );
  });

  // Only valid VIN duplicates are actionable in the duplicate-selection step.
  const duplicateVehicles = computed<DetectedVehicle[]>(() => {
    return validVehicles.value.filter((vehicle) => vehicle.isDuplicate);
  });

  // Phase 22: Computed - Has duplicates
  const hasDuplicates = computed(() => duplicateVehicles.value.length > 0);

  const vehiclesSelectedForImport = computed(() => {
    return validVehicles.value.filter(
      (vehicle) =>
        !vehicle.isDuplicate || selectedDuplicates.value.has(vehicle.rowNumber),
    );
  });

  // Validation errors are shown and skipped, but no longer block valid rows.
  const hasCriticalErrors = computed(() => {
    return vehiclesWithValidationErrors.value.length > 0;
  });

  const criticalErrorCount = computed(() => {
    return vehiclesWithValidationErrors.value.length;
  });

  const canContinueFromPreview = computed(() => {
    if (!preview.value || preview.value.vehicles.length === 0) return false;
    return validVehicles.value.length > 0;
  });

  const canExecuteSelectedImport = computed(() =>
    vehiclesSelectedForImport.value.length > 0,
  );

  function recalculatePreviewStats() {
    if (!preview.value) return;
    Object.assign(
      preview.value,
      calculateBatchImportStats(preview.value.vehicles),
    );
  }

  // Actions
  /**
   * Upload CSV file and get validation preview
   */
  async function uploadCSV(
    file: File,
    customMappings?: ColumnMapping[],
  ): Promise<BatchImportPreview> {
    isUploading.value = true;
    error.value = null;
    preview.value = null;

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Add custom mappings if provided
      if (customMappings) {
        formData.append("mappings", JSON.stringify(customMappings));
      }

      const data = await api.post<BatchImportPreview>(
        "/api/batch-import/preview",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => {
            const total = event.total ?? 0;
            const loaded = event.loaded ?? 0;
            const percentage =
              total > 0 ? Math.round((loaded / total) * 100) : 0;

            fileUploadProgress.value = {
              loaded,
              total,
              percentage,
              message: `Upload: ${percentage}%`,
            };
          },
        },
      );

      preview.value = data;
      if (!uploadProgress.value && !processingStarted.value) {
        fileUploadProgress.value = {
          loaded: 1,
          total: 1,
          percentage: 100,
          message: "Upload abgeschlossen, warte auf Verarbeitung...",
        };
        processingPending.value = true;
      }
      // NOTE: isUploading stays true until BATCH_IMPORT_COMPLETED WebSocket event
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to upload CSV";
      fileUploadProgress.value = null;
      processingPending.value = false;
      processingStarted.value = false;
      isUploading.value = false; // Only reset on error
      throw err;
    }
  }

  /**
   * Create jobs from validated import data
   * Phase 22: Added vehiclesToSkip for selective duplicate handling
   */
  async function executeImport(
    options: BatchImportOptions,
  ): Promise<BatchImportResult> {
    if (!preview.value) {
      throw new Error("No preview data available");
    }

    isProcessing.value = true;
    error.value = null;
    result.value = null;

    try {
      // Phase 22: Calculate which duplicates to skip
      // Skip = all duplicates MINUS selected ones
      const allDuplicateRowNumbers = duplicateVehicles.value.map(
        (v) => v.rowNumber,
      );
      const vehiclesToSkip = allDuplicateRowNumbers.filter(
        (rowNum) => !selectedDuplicates.value.has(rowNum),
      );

      const data = await api.post<BatchImportResult>(
        "/api/batch-import/execute",
        {
          vehicles: preview.value.vehicles,
          options,
          vehiclesToSkip, // Phase 22: Send row numbers to skip
        },
        { timeout: 300000 },
      );

      result.value = data;
      return data;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to create batch jobs";
      throw err;
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * Update edited cell value (Phase 22.3: Extended for all fields)
   */
  function updateCellValue(rowNumber: number, field: string, value: string) {
    const key = `${rowNumber}`;
    if (!editedValues.value[key]) {
      editedValues.value[key] = {};
    }
    editedValues.value[key][field] = value;

    // Update the vehicle in preview
    if (preview.value) {
      const vehicle = preview.value.vehicles.find(
        (v) => v.rowNumber === rowNumber,
      );
      if (vehicle) {
        // Update raw data (this is used for CSV column values)
        (vehicle.rawData as Record<string, unknown>)[field] = value;

        // Update the parsed field if it exists on the vehicle object
        if (field === "vin") {
          vehicle.vin = value;

          // Client-side VIN validation (Phase 22.3 Bug Fix)
          const vinError = vehicle.errors.find((err) => err.field === "vin");
          if (value && value.length !== 17) {
            // Add or update VIN error
            if (vinError) {
              vinError.value = value;
              vinError.message = "VIN must be exactly 17 characters";
            } else {
              vehicle.errors.push({
                row: rowNumber,
                field: "vin",
                value: value,
                message: "VIN must be exactly 17 characters",
                severity: "error",
              });
            }
          } else if (vinError) {
            // Remove VIN error if now valid
            const index = vehicle.errors.indexOf(vinError);
            if (index > -1) vehicle.errors.splice(index, 1);
          }
        }
        if (field === "customId") vehicle.customId = value;
        if (field === "brand") vehicle.brand = value;
        if (field === "model") vehicle.model = value;
        if (field === "fuel") vehicle.fuel = value;
        if (field === "transmission") vehicle.transmission = value;
        if (field === "vehicleType") vehicle.vehicleType = value;
        if (field === "taxation") vehicle.taxation = value;
        if (field === "mileageFrom") {
          const parsed = parseInt(value.replace(/\D/g, ""));
          vehicle.mileageFrom = !isNaN(parsed) ? parsed : undefined;
        }
        if (field === "powerFrom") {
          const parsed = parseInt(value.replace(/\D/g, ""));
          vehicle.powerFrom = !isNaN(parsed) ? parsed : undefined;
        }
        if (field === "registrationFrom") {
          vehicle.registrationFrom = value;
        }

        recalculatePreviewStats();
      }
    }
  }

  /**
   * Get edited value or original
   */
  function getCellValue(rowNumber: number, field: string): string | null {
    const key = `${rowNumber}`;
    return editedValues.value[key]?.[field] ?? null;
  }

  /**
   * Delete a row from preview (Phase 22.3)
   */
  function deleteRow(rowNumber: number) {
    if (!preview.value) return;

    // Remove from vehicles array
    const vehicles = preview.value.vehicles.filter(
      (v) => v.rowNumber !== rowNumber,
    );

    // Recalculate stats
    const { validRows, errorRows, warningRows } =
      calculateBatchImportStats(vehicles);

    preview.value = {
      ...preview.value,
      vehicles,
      validRows,
      errorRows,
      warningRows,
      totalRows: vehicles.length,
    };

    // Remove from duplicates selection if exists
    selectedDuplicates.value = new Set(
      Array.from(selectedDuplicates.value).filter(
        (value) => value !== rowNumber,
      ),
    );

    // Remove from edited values
    delete editedValues.value[`${rowNumber}`];
  }

  /**
   * Reset store state
   */
  function reset() {
    preview.value = null;
    result.value = null;
    error.value = null;
    isUploading.value = false;
    isProcessing.value = false;
    uploadProgress.value = null; // Phase 22.3: Clear progress
    fileUploadProgress.value = null; // Phase 22.3: Clear upload progress
    processingPending.value = false; // Phase 22.3: Clear processing wait
    processingStarted.value = false; // Phase 22.3: Clear processing started
    nextStepAfterProcessing.value = null; // Phase 22.3: Clear pending step
    selectedDuplicates.value = new Set(); // Phase 22: Clear duplicate selections
    editedValues.value = {}; // Clear edited values
  }

  /**
   * Setup WebSocket listeners (Phase 22.3)
   */
  function setupWebSocketListeners(socket: Socket) {
    // Phase 22.3: Join user room for batch import progress events
    const authStore = useAuthStore();
    if (authStore.user?.id) {
      socket.emit("join", authStore.user.id);
    }

    socket.on(
      WSEvent.BATCH_IMPORT_PROGRESS,
      (payload: {
        userId: number;
        current: number;
        total: number;
        percentage: number;
        message: string;
      }) => {
        uploadProgress.value = {
          current: payload.current,
          total: payload.total,
          percentage: payload.percentage,
          message: payload.message,
        };
      },
    );

    socket.on(WSEvent.BATCH_IMPORT_COMPLETED, () => {
      // Clear progress after a delay
      setTimeout(() => {
        uploadProgress.value = null;
      }, 1000);
    });

    socket.on(
      WSEvent.BATCH_IMPORT_ERROR,
      (payload: { userId: number; error: string }) => {
        console.error("[BatchImport] Error event received:", payload);
        error.value = payload.error;
        uploadProgress.value = null;
      },
    );
  }

  return {
    // State
    isUploading,
    isProcessing,
    preview,
    result,
    error,
    uploadProgress, // Phase 22.3
    fileUploadProgress, // Phase 22.3
    processingPending, // Phase 22.3
    processingStarted, // Phase 22.3
    nextStepAfterProcessing, // Phase 22.3

    // Phase 22: Duplicate handling
    selectedDuplicates,
    validVehicles,
    duplicateVehicles,
    vehiclesWithValidationErrors,
    vehiclesSelectedForImport,
    hasDuplicates,
    hasCriticalErrors, // Phase 22.3
    criticalErrorCount, // Phase 22.3
    canContinueFromPreview,
    canExecuteSelectedImport,

    // Inline editing
    editedValues,
    updateCellValue,
    getCellValue,
    deleteRow, // Phase 22.3

    // Actions
    uploadCSV,
    executeImport,
    reset,
    setupWebSocketListeners, // Phase 22.3
  };
});
